import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useContext,
  useImperativeHandle,
  useState,
} from 'react';
import classNames from 'classnames';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { Button, Modal } from 'ncoded-component-library';
import {
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { popSuccess } from 'store/slices/popNotifications.slice';
import env from 'env';
import useAppDispatch from 'hooks/useAppDispatch';
import { useTranslation } from 'react-i18next';
import PaymentMethodsContext from 'providers/PaymentMethods/PaymentMethods.context';
import api from 'api';

import './AddBankAccountMethodModal.styles.scss';

type AddBankAccountMethodModalProps = {
  className?: string;
};

const AddBankAccountMethodModal: ForwardRefRenderFunction<
  ModalRef,
  AddBankAccountMethodModalProps
> = (props, ref) => {
  const { className } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [errMessage, setErrMessage] = useState('');

  const { updatePaymentInfo } = useContext(PaymentMethodsContext);

  const stripe = useStripe();

  const elements = useElements();

  const { t } = useTranslation();

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const dispatch = useAppDispatch();

  const handlePayment = useCallback(async () => {
    if (!stripe || !elements) return;

    setIsLoading(true);
    setErrMessage('');

    const { error } = await stripe.confirmSetup({
      elements,
      redirect: 'if_required',
      confirmParams: {
        return_url: env.PLATFORM_ENDPOINT,
      },
    });

    if (error) {
      setErrMessage(error.message);
      setIsLoading(false);
      return;
    }

    try {
      const { data } = await api.schools.getPaymentInfo();
      updatePaymentInfo(data);
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(popSuccess(t('paymentMethodUpdated')));
      modal.close();
    }

    setIsLoading(false);
  }, [dispatch, elements, modal, stripe, t, updatePaymentInfo]);

  const classes = classNames('add-bank-account-modal', className);

  return (
    <Modal
      className={classes}
      ref={modalRef}
      title={t('AddBanAccountMethodModal.title')}
      footer={
        <Button type="submit" onClick={handlePayment} disabled={isLoading}>
          {t('AddBanAccountMethodModal.submit')}
        </Button>
      }
    >
      {errMessage && <p className="error">{errMessage}</p>}
      <div>
        <PaymentElement />
      </div>
    </Modal>
  );
};

export default forwardRef(AddBankAccountMethodModal);
